import i18n from "../i18n";

/**
 * Corps d'erreur métier renvoyé par l'API Center sur un HTTP 400.
 * Pendant de `BusinessErrorResponse` côté back.
 */
export interface IBusinessError {
    /** Code métier stable, résolu dans le catalogue `errors.*`. Absent tant que le point de levée n'est pas qualifié. */
    code?: string | null;
    /** Message français d'origine, servant de repli d'affichage. */
    message?: string | null;
}

/**
 * Traduit le corps d'erreur d'une réponse API en message affichable.
 *
 * L'API renvoyait auparavant une chaîne nue : chaque message d'exception écrit en français
 * côté serveur devenait du texte utilisateur non traduisible, et changeait dès que quelqu'un
 * reformulait la phrase. Elle renvoie désormais `{ code, message }`, le code étant la donnée
 * stable.
 *
 * Ordre de résolution :
 * 1. `errors.<code>` s'il existe dans le catalogue — c'est le cas nominal ;
 * 2. `message` renvoyé par le serveur — point de levée pas encore qualifié, ou code sans
 *    libellé côté front (un back plus récent que le front) ;
 * 3. la charge utile elle-même si c'est une chaîne — ancienne forme de réponse ;
 * 4. un message générique.
 *
 * La fonction ne suppose jamais la forme reçue : elle doit rester tolérante tant que tous les
 * points de levée n'ont pas de code, et tant que des versions différentes du back peuvent
 * répondre. Voir Documents/Audit_i18n/AUDIT_i18n.md §4.
 *
 * @param payload Corps de la réponse (`error.response.data`).
 * @returns Un message destiné à l'utilisateur, jamais vide.
 */
export const resolveApiErrorMessage = (payload: unknown): string => {
    if (payload == null) return i18n.global.t("errors.unexpected");

    // Ancienne forme : chaîne nue.
    if (typeof payload === "string") {
        return payload.trim() !== "" ? payload : i18n.global.t("errors.unexpected");
    }

    if (typeof payload === "object") {
        const body = payload as IBusinessError & { errors?: Record<string, string[] | string> };

        if (body.code) {
            const key = `errors.${body.code}`;
            if (i18n.global.te(key)) return i18n.global.t(key);
        }

        if (body.message && body.message.trim() !== "") return body.message;

        // ValidationProblemDetails d'ASP.NET : { errors: { champ: [messages] } }
        if (body.errors && typeof body.errors === "object") {
            const flat = Object.values(body.errors)
                .flatMap((v) => (Array.isArray(v) ? v : [v]))
                .filter((v): v is string => typeof v === "string" && v.trim() !== "");
            if (flat.length > 0) return flat.join("\n");
        }
    }

    return i18n.global.t("errors.unexpected");
};

/**
 * Extrait le message affichable d'une erreur axios, quelle que soit la forme de la réponse.
 *
 * @param error Erreur remontée par axios (ou toute erreur portant `response.data`).
 */
export const resolveAxiosErrorMessage = (error: unknown): string => {
    const response = (error as { response?: { data?: unknown } })?.response;
    if (response !== undefined) return resolveApiErrorMessage(response.data);

    const message = (error as { message?: string })?.message;
    if (message && message.trim() !== "") return message;

    return i18n.global.t("errors.unexpected");
};
